/**
 * useTableExport Hook
 * 
 * Encapsulates the logic for exporting table data to Excel (XLSX).
 */

import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { stripMarkdown } from '@/shared/lib/markdown';
import i18n from '@/shared/lib/i18n';

interface UseTableExportOptions {
    headers: string[];
    rows: string[][];
    filename?: string;
}

interface UseTableExportReturn {
    handleExport: () => void;
    isExporting: boolean;
    exportSuccess: boolean;
}

/**
 * Hook for exporting table data to Excel.
 * 
 * @param options - Table headers, rows, and optional filename
 * @returns Export handler and status flags
 * 
 * @example
 * ```tsx
 * const { handleExport, isExporting, exportSuccess } = useTableExport({
 *   headers: ['Name', 'Value'],
 *   rows: [['Item 1', '100'], ['Item 2', '200']],
 * });
 * ```
 */
export function useTableExport(options: UseTableExportOptions): UseTableExportReturn {
    const { headers, rows, filename } = options;

    const [isExporting, setIsExporting] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);

    const handleExport = useCallback(() => {
        if (headers.length === 0 || rows.length === 0) {
            return;
        }

        setIsExporting(true);

        try {
            // Prepare data: strip markdown from all cells
            const excelData = [
                headers.map(h => stripMarkdown(h)),
                ...rows.map(row => row.map(cell => stripMarkdown(cell)))
            ];

            // Create workbook
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet(excelData);

            // Auto-size columns based on content
            const columnWidths = headers.map((header, colIndex) => {
                const headerLength = stripMarkdown(header).length;
                const maxDataLength = Math.max(
                    ...rows.map(row => (row[colIndex] ? stripMarkdown(row[colIndex]).length : 0))
                );
                // Add padding and set minimum width
                return { wch: Math.max(headerLength, maxDataLength, 10) + 2 };
            });
            worksheet['!cols'] = columnWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, i18n.t('common:worksheetName'));

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 10);
            const exportFilename = filename || `tabela_${timestamp}.xlsx`;

            // Trigger download
            XLSX.writeFile(workbook, exportFilename);

            // Show success feedback
            setExportSuccess(true);
            setTimeout(() => setExportSuccess(false), 2000);

        } catch (error) {
            console.error('[useTableExport] Export failed:', error);
            // Could integrate with a toast notification system here
        } finally {
            setIsExporting(false);
        }
    }, [headers, rows, filename]);

    return {
        handleExport,
        isExporting,
        exportSuccess,
    };
}
